import { Injectable, inject } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TaxControllerService } from '@app/api/goods-service/api/taxController.service';
import { PageInfoTaxCategoryVO } from '@app/api/goods-service/model/pageInfoTaxCategoryVO';
import { TaxCategoryRequestDTO } from '@app/api/goods-service/model/taxCategoryRequestDTO';
import { TaxCategoryVO } from '@app/api/goods-service/model/taxCategoryVO';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { formatProductTaxCategoryDisplay } from '@app/feature/center-business/goods/shared/product-tax/product-tax.util';
import {
  toExclusiveLocalDayEndInstant,
  toLocalDayStartInstant,
} from '../utils/tax-category-date.util';

export interface TaxCategory {
  id: number;
  taxCode: string;
  taxName?: string | null;
  taxRate: number;
  activeFlag: boolean;
  effectiveFrom: string;
  effectiveTo: string;
  remark?: string | null;
  objectVersionNumber?: number | null;
}

export interface TaxCategoryPayload {
  taxCode: string;
  taxName?: string | null;
  taxRate: number;
  activeFlag: boolean;
  effectiveFrom: Date;
  effectiveTo: Date;
  remark?: string | null;
  objectVersionNumber?: number | null;
}

export interface TaxCategorySearchParams {
  taxCode?: string;
  taxName?: string;
  activeFlag?: boolean | null;
  effectiveAt?: string | Date | null;
  page?: number;
  size?: number;
  sortBy?: string;
  ascending?: boolean;
}

export interface TaxCategoryOption {
  label: string;
  value: number;
  taxCode: string;
  taxName?: string | null;
  taxRate: number;
  effectiveFrom: string;
  effectiveTo: string;
}

export interface TaxCategoryPage {
  items: TaxCategory[];
  totalRecords: number;
}

const DEFAULT_OPTION_PAGE_SIZE = 100;

@Injectable({
  providedIn: 'root',
})
export class TaxCategoryService {
  private readonly taxController = inject(TaxControllerService);

  search(params: TaxCategorySearchParams): Observable<TaxCategoryPage> {
    return this.taxController
      .page(
        this.emptyToUndefined(params.taxCode),
        this.emptyToUndefined(params.taxName),
        params.activeFlag ?? undefined,
        toLocalDayStartInstant(params.effectiveAt),
        params.page ?? 0,
        params.size ?? 20,
        params.sortBy ?? 'taxCode',
        params.ascending ?? true,
      )
      .pipe(
        switchMap((response: PageInfoTaxCategoryVO | Blob) =>
          this.handleBlobResponse<PageInfoTaxCategoryVO>(response),
        ),
        map((response: PageInfoTaxCategoryVO) => ({
          items: (response.pageContent ?? []).map((item) =>
            this.mapTaxCategory(item),
          ),
          totalRecords: response.totalElements ?? 0,
        })),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  get(id: number): Observable<TaxCategory> {
    return this.taxController.get(id).pipe(
      switchMap((response: TaxCategoryVO | Blob) =>
        this.handleBlobResponse<TaxCategoryVO>(response),
      ),
      map((response: TaxCategoryVO) => this.mapTaxCategory(response)),
      catchError((error) => this.handleBlobError(error)),
    );
  }

  create(payload: TaxCategoryPayload): Observable<TaxCategory> {
    return this.taxController.create11(this.mapPayload(payload)).pipe(
      switchMap((response: TaxCategoryVO | Blob) =>
        this.handleBlobResponse<TaxCategoryVO>(response),
      ),
      map((response: TaxCategoryVO) => this.mapTaxCategory(response)),
      catchError((error) => this.handleBlobError(error)),
    );
  }

  update(id: number, payload: TaxCategoryPayload): Observable<TaxCategory> {
    return this.taxController.update10(id, this.mapPayload(payload)).pipe(
      switchMap((response: TaxCategoryVO | Blob) =>
        this.handleBlobResponse<TaxCategoryVO>(response),
      ),
      map((response: TaxCategoryVO) => this.mapTaxCategory(response)),
      catchError((error) => this.handleBlobError(error)),
    );
  }

  updateActiveFlag(id: number, activeFlag: boolean): Observable<TaxCategory> {
    return this.taxController.updateActiveFlag(id, activeFlag).pipe(
      switchMap((response: TaxCategoryVO | Blob) =>
        this.handleBlobResponse<TaxCategoryVO>(response),
      ),
      map((response: TaxCategoryVO) => this.mapTaxCategory(response)),
      catchError((error) => this.handleBlobError(error)),
    );
  }

  getActiveOptions(
    effectiveAt: string | Date | null = new Date(),
  ): Observable<TaxCategoryOption[]> {
    return this.search({
      activeFlag: true,
      effectiveAt,
      page: 0,
      size: DEFAULT_OPTION_PAGE_SIZE,
      sortBy: 'taxCode',
      ascending: true,
    }).pipe(map((response) => response.items.map((item) => this.mapOption(item))));
  }

  private mapTaxCategory(vo: TaxCategoryVO): TaxCategory {
    return {
      id: vo.id ?? 0,
      taxCode: vo.taxCode ?? '',
      taxName: vo.taxName ?? null,
      taxRate: vo.taxRate ?? 0,
      activeFlag: vo.activeFlag ?? false,
      effectiveFrom: vo.effectiveFrom ?? '',
      effectiveTo: vo.effectiveTo ?? '',
      remark: vo.remark ?? null,
      objectVersionNumber: vo.objectVersionNumber ?? null,
    };
  }

  private mapPayload(payload: TaxCategoryPayload): TaxCategoryRequestDTO {
    const dto: TaxCategoryRequestDTO = {
      taxCode: payload.taxCode.trim(),
      taxName: this.emptyToUndefined(payload.taxName),
      taxRate: payload.taxRate,
      activeFlag: payload.activeFlag,
      effectiveFrom: toLocalDayStartInstant(payload.effectiveFrom) ?? '',
      effectiveTo: toExclusiveLocalDayEndInstant(payload.effectiveTo) ?? '',
      remark: this.emptyToUndefined(payload.remark),
    };

    if (payload.objectVersionNumber !== null && payload.objectVersionNumber !== undefined) {
      dto.objectVersionNumber = payload.objectVersionNumber;
    }

    return dto;
  }

  private mapOption(item: TaxCategory): TaxCategoryOption {
    return {
      label: formatProductTaxCategoryDisplay(item),
      value: item.id,
      taxCode: item.taxCode,
      taxName: item.taxName,
      taxRate: item.taxRate,
      effectiveFrom: item.effectiveFrom,
      effectiveTo: item.effectiveTo,
    };
  }

  private emptyToUndefined(value?: string | null): string | undefined {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
  }

  private handleBlobResponse<T>(response: Blob | T): Observable<T> {
    if (response instanceof Blob) {
      return from(BlobConverterUtil.convertToJson(response)) as Observable<T>;
    }
    return of(response as T);
  }

  private handleBlobError(error: any): Observable<never> {
    if (error?.error instanceof Blob) {
      return from(BlobConverterUtil.convertToJson(error.error)).pipe(
        catchError(() => throwError(() => error)),
        switchMap((parsedError: any) =>
          throwError(() => ({ ...error, error: parsedError })),
        ),
      );
    }
    return throwError(() => error);
  }
}
